import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShieldAlert, X } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import type { Crumb } from './Breadcrumbs';
import { UploadMonitor } from '@/components/uploads/UploadMonitor';
import { useUIStore } from '@/stores/ui';
import { cn } from '@/lib/cn';
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';
import { useAuth } from '@/hooks/useAuth';
import { useLogout } from '@/api/auth';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { MfaSetupModal } from '@/components/auth/MfaSetupModal';
import { api, apiError } from '@/lib/api';

interface AppShellProps {
  children: ReactNode;
  crumbs?: Crumb[];
}

export function AppShell({ children, crumbs }: AppShellProps) {
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);
  const push = useUIStore((s) => s.pushToast);
  const { user, logout } = useAuth();
  const logoutMut = useLogout();
  const navigate = useNavigate();
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  const handleInactivityWarn = useCallback(() => setShowTimeoutWarning(true), []);
  const handleInactivityLogout = useCallback(async () => {
    setShowTimeoutWarning(false);
    try { await logoutMut.mutateAsync(); } catch { /* ignore */ }
    logout();
    navigate(`/session-expired${window.location.pathname !== '/' ? `?from=${encodeURIComponent(window.location.pathname)}` : ''}`);
  }, [logoutMut, logout, navigate]);

  const { reset: resetInactivityTimer } = useInactivityTimeout({
    onWarn: handleInactivityWarn,
    onLogout: handleInactivityLogout,
    enabled: !!user,
  });

  // An in-progress upload is ACTIVE use, not inactivity — but uploads don't
  // generate mouse/keyboard events. While any upload is running, keep the
  // inactivity timer alive with a heartbeat so a large upload can never trigger
  // an idle logout mid-transfer. (The API layer also refreshes the access token
  // proactively, so the request itself won't 401 either.)
  const activeUploads = useUIStore((s) => s.activeUploads);
  useEffect(() => {
    if (activeUploads <= 0) return;
    resetInactivityTimer();
    const id = window.setInterval(resetInactivityTimer, 60_000);
    return () => window.clearInterval(id);
  }, [activeUploads, resetInactivityTimer]);

  const [mfaBannerDismissed, setMfaBannerDismissed] = useState(false);
  const [mfaModalDismissed, setMfaModalDismissed] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const sendFeedback = useMutation({
    mutationFn: (message: string) => api.post('/feedback', { message }),
    onSuccess: () => { setFeedbackSent(true); setFeedbackText(''); setTimeout(() => { setShowFeedback(false); setFeedbackSent(false); }, 1500); },
    onError: (e) => push({ title: 'Could not send feedback', body: apiError(e), variant: 'error' }),
  });
  const isSsoUser = !!user?.external_provider;
  const showMfaBanner = !!user && user.mfa_enabled === false && !isSsoUser && !mfaBannerDismissed && mfaModalDismissed;
  const showMfaModal = !!user && user.mfa_enabled === false && !isSsoUser && !mfaModalDismissed;

  return (
    <div className="h-screen overflow-hidden flex bg-[#F2F2F8] dark:bg-[#0D0D1A]">
      {/* h-screen + overflow-hidden: the shell fills the viewport exactly so the
          top bar and sidebar stay fixed and ONLY <main> scrolls (sticky header +
          independently-scrolling sidebar). */}
      {/* Mobile backdrop — tapping it closes the drawer */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 md:hidden transition-opacity duration-200',
          sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={() => setSidebarOpen(false)}
        aria-hidden
      />

      {/* Sidebar: fixed overlay on mobile (no flex space), static flex item on desktop */}
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar crumbs={crumbs} />
        {showMfaBanner && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-sm text-amber-900">
            <ShieldAlert size={15} className="shrink-0 text-amber-600" />
            <span className="flex-1">
              <strong>Secure your account:</strong>{' '}
              Set up two-factor authentication to protect your account from unauthorised access.{' '}
              <Link to="/profile" className="font-semibold underline hover:text-amber-700" onClick={() => setMfaBannerDismissed(true)}>
                Set up now
              </Link>
            </span>
            <button
              type="button"
              onClick={() => setMfaBannerDismissed(true)}
              aria-label="Dismiss"
              className="p-0.5 text-amber-600 hover:text-amber-800 shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <main className="flex-1 min-w-0 overflow-y-auto custom-scrollbar p-4 md:p-6 lg:p-8">
          {children}
        </main>
        <footer className="shrink-0 border-t border-[#E3E3F1] dark:border-[#2A2A4A] px-4 py-2.5 text-center text-[11px] text-[#BABACE] dark:text-[#5A5A7A]">
          Made with&nbsp;❤️&nbsp;by{' '}
          <a href="https://billerbay.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#676687] transition-colors">BillerBay</a>
          {' '}&amp;{' '}
          <a href="https://inetro.net" target="_blank" rel="noopener noreferrer" className="hover:text-[#676687] transition-colors">iNetro</a>
          {' '}·{' '}© {new Date().getFullYear()} BillerBay. All rights reserved.
          {' '}·{' '}
          <button
            type="button"
            onClick={() => setShowFeedback(true)}
            className="hover:text-[#676687] dark:hover:text-[#8888AA] transition-colors underline underline-offset-2"
          >
            Send feedback
          </button>
        </footer>
      </div>
      <UploadMonitor />

      {showMfaModal && (
        <MfaSetupModal
          onDone={() => { setMfaModalDismissed(true); setMfaBannerDismissed(false); }}
          allowSkip
        />
      )}

      <Modal
        open={showFeedback}
        onClose={() => { if (!sendFeedback.isPending) { setShowFeedback(false); setFeedbackText(''); setFeedbackSent(false); } }}
        title="Send feedback"
        footer={
          !feedbackSent ? (
            <>
              <Button variant="secondary" disabled={sendFeedback.isPending} onClick={() => { setShowFeedback(false); setFeedbackText(''); }}>Cancel</Button>
              <Button
                variant="primary"
                disabled={!feedbackText.trim() || sendFeedback.isPending}
                loading={sendFeedback.isPending}
                onClick={() => sendFeedback.mutate(feedbackText.trim())}
                className="w-auto"
              >
                Send
              </Button>
            </>
          ) : undefined
        }
      >
        {feedbackSent ? (
          <p className="text-sm text-green-600 text-center py-4">Feedback sent — thank you!</p>
        ) : (
          <>
            <p className="text-xs text-slate-500 mb-3">Share a bug, suggestion, or anything on your mind.</p>
            <textarea
              autoFocus
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="What's on your mind?"
              rows={5}
              className="w-full border border-[#E3E3F1] dark:border-[#2A2A4A] rounded-[6px] px-3 py-2 text-sm text-slate-800 dark:text-white bg-white dark:bg-[#1A1A2E] placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-[#3F4CFF] resize-none"
            />
          </>
        )}
      </Modal>

      <Modal
        open={showTimeoutWarning}
        onClose={() => { setShowTimeoutWarning(false); }}
        title="Session expiring soon"
        footer={
          <>
            <Button variant="secondary" onClick={handleInactivityLogout}>Sign out now</Button>
            <Button variant="primary" onClick={() => { setShowTimeoutWarning(false); resetInactivityTimer(); }}>Stay signed in</Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Your session will expire in 5 minutes due to inactivity. Click <strong>Stay signed in</strong> to continue.
        </p>
      </Modal>
    </div>
  );
}
